import type { PanderrTemplateDefinition } from "../types";

export const POOL_SERVICE_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "pool-service-modern",
  name: "Pool Service Modern",
  description:
    "A polished, image-forward website for pool cleaning, maintenance, repair, renovation, and commercial pool service companies.",
  category: "pool-service",

  navigation: [
    { label: "Services", href: "/services/" },
    { label: "About", href: "/about/" },
    { label: "Gallery", href: "/gallery/" },
    { label: "Contact", href: "/contact/" },
  ],

  headerCta: {
    label: "Request a Quote",
    href: "/contact/",
    variant: "primary",
  },

  footer: {
    description:
      "Professional pool cleaning, maintenance, repairs, renovations, and commercial pool care.",
    columns: [
      {
        heading: "Explore",
        links: [
          { label: "Services", href: "/services/" },
          { label: "About", href: "/about/" },
          { label: "Gallery", href: "/gallery/" },
        ],
      },
      {
        heading: "Get Started",
        links: [
          { label: "Service Area", href: "/contact/" },
          { label: "Request a Quote", href: "/contact/" },
          { label: "Contact", href: "/contact/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Professional pool care",
      heading: "Professional pool care without the hassle.",
      body: "Reliable pool cleaning, maintenance, repairs, and renovations that keep your water clear and your pool ready to enjoy.",

      images: [
        {
          src: "/studio/assets/stock/images/luxury-home-pool.jpg",
          alt: "Luxury residential swimming pool and outdoor living area",
          role: "hero",
        },
      ],

      buttons: [
        {
          label: "Request a Free Quote",
          href: "/contact/",
          variant: "primary",
        },
        {
          label: "Explore Services",
          href: "/services/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "Reliable. Professional. Consistent.",
      heading: "Pool service you can count on week after week.",
      body: "Clear communication, dependable scheduling, experienced care, and attention to the details that keep your pool healthy, beautiful, and ready to use.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "Pool services",
      heading: "Everything your pool needs in one place.",
      body: "From routine care to repairs and renovations, we help homeowners and commercial properties protect their pools and enjoy them with less work.",

      services: [
        {
          title: "Weekly Pool Service",
          description:
            "Routine cleaning, chemical balancing, equipment checks, and dependable ongoing care.",
          image: {
            src: "/studio/assets/stock/images/pool-blue-tile.jpg",
            alt: "Clean blue swimming pool water and tile",
            role: "service",
          },
          href: "/services/",
        },

        {
          title: "Equipment & Repairs",
          description:
            "Troubleshooting and repair for pumps, filters, heaters, plumbing, and other pool equipment.",
          image: {
            src: "/studio/assets/stock/images/pool-steps-water.jpg",
            alt: "Swimming pool steps in clear blue water",
            role: "service",
          },
          href: "/services/",
        },

        {
          title: "Water Care",
          description:
            "Professional water testing and treatment to help keep your pool clean, clear, balanced, and comfortable.",
          image: {
            src: "/studio/assets/stock/images/pool-water-ripples.jpg",
            alt: "Clear blue swimming pool water with ripples",
            role: "service",
          },
          href: "/services/",
        },

        {
          title: "Pool Renovations",
          description:
            "Refresh aging pools with resurfacing, tile, coping, equipment upgrades, and outdoor improvements.",
          image: {
            src: "/studio/assets/stock/images/modern-patio-pool.jpg",
            alt: "Modern residential pool and renovated outdoor patio",
            role: "service",
          },
          href: "/services/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "More time enjoying your pool",
      heading:
        "Spend less time maintaining your pool and more time enjoying it.",
      body: "Owning a pool should feel like an escape, not another chore. We make pool care straightforward with dependable service, clear communication, and proactive attention to your water and equipment.",

      images: [
        {
          src: "/studio/assets/stock/images/pool-backyard-retreat.jpg",
          alt: "Beautiful backyard swimming pool and outdoor retreat",
          role: "about",
        },
      ],

      buttons: [
        {
          label: "Learn About Our Company",
          href: "/about/",
          variant: "text",
        },
      ],
    },

    {
      id: "commercial",
      type: "about",
      eyebrow: "Commercial pool service",
      heading: "Professional care for pools that serve entire communities.",
      body: "We provide dependable pool service for HOAs, apartment communities, hotels, fitness facilities, municipalities, and other commercial properties that need consistent care and responsive support.",

      images: [
        {
          src: "/studio/assets/stock/images/commercial-lap-pool.jpg",
          alt: "Commercial swimming pool with lap lanes",
          role: "about",
        },
      ],

      buttons: [
        {
          label: "Ask About Commercial Service",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "process",
      type: "process",
      eyebrow: "Simple pool care",
      heading: "Getting dependable pool service should be easy.",
      body: "Tell us about your pool, receive a clear recommendation, choose the service that fits your needs, and let our team take care of the rest.",
    },

    {
      id: "service-area",
      type: "service-area",
      eyebrow: "Serving local pool owners",
      heading: "Professional pool service where you need it.",
      body: "We serve homeowners, neighborhoods, property managers, and commercial facilities throughout the surrounding area. Contact us to confirm availability for your location.",

      buttons: [
        {
          label: "Check Your Service Area",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Ready for a better pool?",
      heading: "Enjoy a cleaner, clearer, better-cared-for pool.",
      body: "Request a quote today and let us help make pool ownership easier.",

      images: [
        {
          src: "/studio/assets/stock/images/resort-infinity-pool.jpg",
          alt: "Beautiful infinity swimming pool",
          role: "cta",
        },
      ],

      buttons: [
        {
          label: "Request a Free Quote",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "about",
      name: "About",
      title: "About Us",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About our company",
          heading:
            "Pool care built around consistency, communication, and trust.",
          body: "We help homeowners and commercial properties enjoy cleaner, safer, better-maintained pools with dependable professional service.",
          images: [
            {
              src: "/studio/assets/stock/images/poolside-relaxation.jpg",
              alt: "Relaxing poolside setting",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "about-story",
          type: "about",
          eyebrow: "Our approach",
          heading: "Your pool should be ready when you are.",
          body: "We believe great pool service is about more than cleaning water. It means showing up consistently, watching for problems before they become expensive, communicating clearly, and taking pride in every pool we care for.",
          images: [
            {
              src: "/studio/assets/stock/images/pool-family-swimming.jpg",
              alt: "Family enjoying a clean residential swimming pool",
              role: "about",
            },
          ],
        },

        {
          id: "about-trust",
          type: "trust",
          eyebrow: "What matters to us",
          heading: "Dependable service. Clear communication. Quality care.",
          body: "From routine maintenance to unexpected repairs, our goal is to make caring for your pool straightforward and dependable.",
        },

        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Let's talk",
          heading: "Ready to make pool ownership easier?",
          body: "Tell us about your pool and we'll help you determine the best service for your needs.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "services",
      name: "Services",
      title: "Pool Services",
      slug: "/services/",
      sections: [
        {
          id: "services-hero",
          type: "hero",
          eyebrow: "Pool services",
          heading: "Complete care for residential and commercial pools.",
          body: "From weekly cleaning and water care to equipment repair and renovation, we provide the professional support your pool needs.",
          images: [
            {
              src: "/studio/assets/stock/images/pool-aerial-deck.jpg",
              alt: "Swimming pool and surrounding deck viewed from above",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "services-list",
          type: "services",
          eyebrow: "What we do",
          heading: "Professional service for every stage of pool ownership.",
          body: "Choose the service that best fits your pool, property, and ongoing maintenance needs.",
          services: [
            {
              title: "Weekly Cleaning & Maintenance",
              description:
                "Scheduled cleaning, brushing, skimming, chemical balancing, equipment checks, and ongoing pool care.",
              image: {
                src: "/studio/assets/stock/images/pool-water-texture.jpg",
                alt: "Clean blue swimming pool water",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Equipment Repair",
              description:
                "Troubleshooting and repair for pumps, filters, heaters, plumbing, automation, and other equipment.",
              image: {
                src: "/studio/assets/stock/images/pool-underwater-tile.jpg",
                alt: "Swimming pool tile viewed underwater",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Pool Renovations",
              description:
                "Update an aging pool with resurfacing, tile, coping, equipment upgrades, and aesthetic improvements.",
              image: {
                src: "/studio/assets/stock/images/luxury-home-pool.jpg",
                alt: "Renovated luxury residential swimming pool",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Commercial Pool Service",
              description:
                "Reliable ongoing service for HOAs, apartments, hospitality properties, municipalities, and other facilities.",
              image: {
                src: "/studio/assets/stock/images/lap-pool-lanes.jpg",
                alt: "Commercial swimming pool with lap lanes",
                role: "service",
              },
              href: "/contact/",
            },
          ],
        },

        {
          id: "services-process",
          type: "process",
          eyebrow: "How it works",
          heading: "Professional care without the complicated process.",
          body: "Tell us about your pool, choose the right service plan, schedule your service, and enjoy dependable ongoing care.",
        },

        {
          id: "services-cta",
          type: "cta",
          eyebrow: "Ready to get started?",
          heading: "Let's build the right service plan for your pool.",
          body: "Reach out today and tell us what your pool needs.",
          buttons: [
            {
              label: "Request a Free Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "gallery",
      name: "Gallery",
      title: "Pool Gallery",
      slug: "/gallery/",
      sections: [
        {
          id: "work-hero",
          type: "hero",
          eyebrow: "Pool inspiration",
          heading: "Beautiful pools are meant to be enjoyed.",
          body: "Use this gallery to showcase pools, renovations, commercial properties, completed projects, and the results of professional pool care.",
          images: [
            {
              src: "/studio/assets/stock/images/resort-pool-loungers.jpg",
              alt: "Beautiful swimming pool with poolside lounge chairs",
              role: "hero",
            },
          ],
        },

        {
          id: "work-projects",
          type: "projects",
          eyebrow: "Featured pools",
          heading: "Clean water. Beautiful spaces. Professional care.",
          body: "Replace these starter images with your own projects, customer pools, renovation work, and before-and-after results.",
          projects: [
            {
              title: "Residential Pool Care",
              description:
                "Ongoing professional maintenance for a clean, clear, inviting backyard pool.",
              image: {
                src: "/studio/assets/stock/images/pool-float-modern-home.jpg",
                alt: "Modern residential swimming pool",
                role: "project",
              },
            },
            {
              title: "Pool Renovation",
              description:
                "A refreshed pool and outdoor environment designed for years of enjoyment.",
              image: {
                src: "/studio/assets/stock/images/modern-patio-pool.jpg",
                alt: "Modern pool and outdoor patio",
                role: "project",
              },
            },
            {
              title: "Commercial Pool Care",
              description:
                "Consistent professional care for a high-use commercial swimming pool.",
              image: {
                src: "/studio/assets/stock/images/lap-pool-swimmer.jpg",
                alt: "Swimmer in a professionally maintained lap pool",
                role: "project",
              },
            },
          ],
        },

        {
          id: "work-cta",
          type: "cta",
          eyebrow: "Have a pool that needs attention?",
          heading: "Let's make your pool easier to enjoy.",
          body: "Request a quote and tell us about your maintenance, repair, or renovation needs.",
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact Us",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Get in touch",
          heading: "Tell us what your pool needs.",
          body: "Need weekly service, help with an equipment problem, commercial pool care, or an estimate for a renovation? Reach out and we'll help you determine the next step.",
          images: [
            {
              src: "/studio/assets/stock/images/pool-yellow-float.jpg",
              alt: "Bright swimming pool with floating pool accessory",
              role: "hero",
            },
          ],
        },
      ],
    },
  ],
};
